import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import ProductCard from "@surgia/ui/containers/productCard";
import Button from "@surgia/ui/components/button";
import { tenantQueries } from "@surgia/api/src/queryStore/shared/tenant";
import { useParams, useLocation } from "react-router-dom";
import { TenantProductDTO } from "../../../../../../packages/contract/dtos";
import { useAssignParentProduct } from "../../../hooks/useAssignParentProduct";
import { lookupQueries } from "@surgia/api/src/queryStore/shared/lookup";
import { tenantQueries as adminTenantQueries } from "@surgia/api/src/queryStore/admin/tenant";

function DetailsConfiguration({
  selectedProducts,
  onPreviousStep,
  onNextStep,
  isAssigning = false,
}: {
  selectedProducts: any[];
  onPreviousStep: () => void;
  onNextStep: () => Promise<void>;
  isAssigning?: boolean;
}) {
  const { t } = useTranslation();
  const { id: tenantId } = useParams();
  const location = useLocation();
  
  // Try to get currency from navigation state first (if navigated from TenantDetails)
  const currencyFromState = location.state?.currencyCode;

  // Use the assign parent product hook
  const { assignParentProducts, isAssigning: isAssigningParentProduct } =
    useAssignParentProduct({
      tenantId: tenantId!,
    });

  // Fetch tenant data to get storefront_id
  const { data: tenantResponse } = useQuery({
    ...adminTenantQueries.queries.tenants({
      status: "ACTIVE",
      id: tenantId ? +tenantId : undefined,
    }),
  });

  // Get storefront_id from tenant response
  const storefront_id = tenantResponse?.data?.[0]?.storefront_id;

  // Fetch storefront currency - will use cache if already fetched in TenantDetails
  const { data: storefrontCurrencyData } = useQuery({
    ...lookupQueries.queries.storefrontCurrency({
      storefront_id: Number(storefront_id) || 0,
      is_base_currency: true,
    }),
    enabled: !!storefront_id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Get currency code - prefer from state, then cache
  const currencyCode = 
    currencyFromState || 
    storefrontCurrencyData?.[0]?.currency?.code;

  const { data: tenantProductsResponse, isLoading: isTenantProductsLoading } =
    useQuery({
      ...tenantQueries.queries.tenantProducts({
        tenant_id: +tenantId!,
        component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
        product_id: selectedProducts.join(","),
        currency_code: currencyCode,
      }),
      enabled: selectedProducts.length > 0,
      select: (data) => {
        const parentProductCurrentlyAssigned =
          data?.data?.[0]?.currently_assigned;
        return {
          total: data.total,
          data: data.data.map((tenantProduct: TenantProductDTO) => ({
            parentProductCurrentlyAssigned,
            id: tenantProduct.product_id.toString(),
            name: tenantProduct.catalog_info.name,
            variantCount:
              parseInt(tenantProduct.catalog_info.variants_count) || 0,
            image: tenantProduct.catalog_info?.product_images?.[0]?.image_path,
            isSelected: selectedProducts.includes(
              tenantProduct.product_id.toString()
            ),
            variants: [], // This will be populated by the ProductCard component
            secondId: tenantProduct?.id.toString(),
            // Additional data that might be useful
            tenantProductId: tenantProduct?.id,
            tenantProductName: tenantProduct.tenant_product_name,
            tenantProductSku: tenantProduct.tenant_product_sku,
            price: tenantProduct.catalog_info?.price,
            sku: tenantProduct.catalog_info.sku,
            description: tenantProduct.catalog_info.description,
            status: tenantProduct.catalog_info.status,
          })),
        };
      },
    });

  const handleAssignParentProduct = async (productId: string) => {
    try {
      await assignParentProducts({
        parentProductIds: [parseInt(productId)],
      });
    } catch (error) {
      console.error("Error assigning parent product:", error);
    }
  };

  if (isTenantProductsLoading) {
    return (
      <div className="flex-1 px-6 h-full pt-6">
        <div className="space-y-4">
          {/* Product Card Skeletons */}
          {[...Array(3)].map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Product image skeleton */}
                  <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                  <div className="flex items-center gap-2">
                    {/* Product name skeleton */}
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                </div>
                {/* Expand arrow skeleton */}
                <div className="w-4 h-4 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="flex-1 px-6 flex flex-col h-full pt-6">
      <div className="space-y-4 flex-1">
        {tenantProductsResponse?.data?.map((product: any) => (
          <ProductCard
            key={product?.id}
            product={product}
            onAssignParentProduct={handleAssignParentProduct}
            isAssigningParentProduct={isAssigningParentProduct}
            tenantId={tenantId}
          />
        ))}
      </div>

      {tenantProductsResponse?.data?.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>{t("admin.productAssignment.messages.noProductsFound")}</p>
        </div>
      )}

      {/* Pagination Footer */}
      <div className="flex flex-row justify-end items-end px-8 py-3 pb-4 gap-3 w-full h-16 border-t border-[#E9EAEB] mt-auto">
        <div className="flex-1 flex items-center">
          <span className="text-sm text-gray-600">
            {tenantProductsResponse?.data?.length || 0} Products selected
          </span>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" onClick={onPreviousStep}>
            ← Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                await onNextStep();
              } catch (error) {
                console.error("Error proceeding to next step:", error);
              }
            }}
            disabled={isAssigning}
          >
            {isAssigning ? "Loading..." : "Next →"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DetailsConfiguration;
